/**
 * useUpdateDataAgent Hook
 *
 * React Query mutation hook for updating a data agent.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dataAgentService } from '../../../api/data-agent.service';
import { DATA_AGENTS_QUERY_KEY } from './useDataAgents';
import { DATA_AGENT_DETAILS_QUERY_KEY } from './useDataAgentDetails';
import type { DataAgentUpdateDTO, DataAgent } from '../../../types/data-agent.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseUpdateDataAgentOptions {
  onSuccess?: (dataAgent: DataAgent) => void;
  onError?: (error: string) => void;
}

export function useUpdateDataAgent(
  tenantId: string,
  dataAgentId: string,
  options?: UseUpdateDataAgentOptions
) {
  const queryClient = useQueryClient();

  return useMutation<DataAgent, Error, DataAgentUpdateDTO>({
    mutationFn: (data: DataAgentUpdateDTO) =>
      dataAgentService.updateDataAgent(tenantId, dataAgentId, data),
    onSuccess: (dataAgent) => {
      queryClient.invalidateQueries({ queryKey: [DATA_AGENTS_QUERY_KEY, tenantId] });
      queryClient.invalidateQueries({
        queryKey: [DATA_AGENT_DETAILS_QUERY_KEY, tenantId, dataAgentId],
      });
      options?.onSuccess?.(dataAgent);
    },
    onError: (error) => {
      options?.onError?.(getErrorMessage(error));
    },
  });
}
